import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "../lib/cn";

/**
 * A content panel on the page sheet: white, 12px corners, an AE Black 100 edge, the Design System's 24px padding and a bold Inter
 * title. `interactive` cards take the Design System card's gold edge and glow on hover; `glass` picks the floating material for cards
 * that sit over busy content (a map, a chart, a hero). For the Design System's gold-edged card use the `card-ds` utility.
 */
export const Card = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & { glass?: boolean; interactive?: boolean }>(
  ({ className, glass, interactive, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="card"
      className={cn(
        glass ? "surface-glass" : "surface",
        interactive && "cursor-pointer transition-[border-color,box-shadow] duration-300 hover:border-aegold-300 hover:shadow-[0_20px_25px_-5px_var(--color-aegold-100),0_8px_10px_-6px_var(--color-aegold-100)]",
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = "Card";

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-1.5 p-6 pb-4", className)} {...props} />
));
CardHeader.displayName = "CardHeader";

export const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(({ className, ...props }, ref) => (
  <h3 ref={ref} className={cn("font-heading text-lg font-bold leading-tight text-aeblack-800", className)} {...props} />
));
CardTitle.displayName = "CardTitle";

export const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center gap-2 p-6 pt-0", className)} {...props} />
));
CardFooter.displayName = "CardFooter";
